// GruendungsdokumentFeld.tsx — Gesellschaftsvertrag bzw. Satzung in der
// Firmenanlage (User 26.09.2026).
//
// Zwei Wege, beide erst NACH dem Anlegen wirksam, weil ein Dokument eine
// Kontakt-Id braucht:
//  - selbst hochladen: Die Datei wird vorgemerkt und dann in der Akte abgelegt.
//  - aus dem Handelsregister holen: vorgemerkt; nach dem Anlegen holt dasselbe
//    Backend wie „Gesellschaftsvertrag auswerten" in der Akte den Vertrag aus
//    dem Registerordner (firmendaten/gesellschaftsvertrag/:id).

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";

const useStyles = makeStyles({
  feld: { display: "flex", flexDirection: "column", rowGap: "8px" },
  label: { fontFamily: REFINED.fontUI, fontSize: "0.875rem", fontWeight: 600, color: REFINED.ink },
  zeile: { display: "flex", flexWrap: "wrap", alignItems: "center", ...shorthands.gap("10px") },
  knopf: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    ...shorthands.padding("8px", "14px"),
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
    ":disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
  knopfAn: { backgroundColor: REFINED.aub50 },
  vorgemerkt: {
    display: "inline-flex",
    alignItems: "center",
    ...shorthands.gap("8px"),
    fontFamily: REFINED.fontUI,
    fontSize: "0.85rem",
    color: REFINED.ink,
  },
  weg: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    fontWeight: 600,
    cursor: "pointer",
    ...shorthands.padding("0"),
  },
  hinweis: { fontFamily: REFINED.fontUI, fontSize: "0.8rem", color: REFINED.ink3 },
});

export function GruendungsdokumentFeld({
  istVerein,
  registerName,
  registerMoeglich,
  registerFehltHinweis,
  datei,
  onDatei,
  abgelegt,
  ausRegister,
  onAusRegister,
}: {
  /** Verein → „Satzung", sonst „Gesellschaftsvertrag". */
  istVerein: boolean;
  /**
   * Das Register, in dem die Rechtsform steht („Gesellschaftsregister" bei der
   * eGbR, T-329) — Beschriftung des Abruf-Knopfs. Ohne Angabe Vereins- bzw.
   * Handelsregister.
   */
  registerName?: string;
  /** Registernummer und Gericht stehen drin — sonst lässt sich nichts holen. */
  registerMoeglich: boolean;
  /** Warum der Registerabruf gesperrt ist; null = Körperschaft ohne Register. */
  registerFehltHinweis: string | null;
  datei: File | null;
  onDatei: (f: File | null) => void;
  /** Die Datei liegt schon in der Akte (Bearbeiten-Modus) — nicht mehr „vorgemerkt". */
  abgelegt?: boolean;
  ausRegister: boolean;
  onAusRegister: (an: boolean) => void;
}) {
  const s = useStyles();
  const eingabe = React.useRef<HTMLInputElement>(null);
  const name = istVerein ? "Satzung" : "Gesellschaftsvertrag";

  return (
    <div className={s.feld}>
      <span className={s.label}>{name}</span>
      <div className={s.zeile}>
        <input
          ref={eingabe}
          type="file"
          accept="application/pdf,image/*"
          hidden
          onChange={(e) => {
            onDatei(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />
        <button type="button" className={s.knopf} onClick={() => eingabe.current?.click()}>
          {name} hochladen
        </button>
        {registerFehltHinweis !== null && (
          <button
            type="button"
            className={mergeClasses(s.knopf, ausRegister && s.knopfAn)}
            disabled={!registerMoeglich && !ausRegister}
            aria-pressed={ausRegister}
            title={registerMoeglich ? undefined : registerFehltHinweis}
            onClick={() => onAusRegister(!ausRegister)}
          >
            {ausRegister ? "✓ " : ""}Aus dem{" "}
            {registerName ?? (istVerein ? "Vereinsregister" : "Handelsregister")} holen
          </button>
        )}
      </div>
      {datei && abgelegt && <span className={s.vorgemerkt}>{datei.name} — in der Akte abgelegt</span>}
      {datei && !abgelegt && (
        <span className={s.vorgemerkt}>
          {datei.name} — wird nach dem Anlegen in der Akte abgelegt
          <button type="button" className={s.weg} onClick={() => onDatei(null)}>
            entfernen
          </button>
        </span>
      )}
      {ausRegister && (
        <span className={s.hinweis}>
          Wird nach dem Anlegen aus dem Registerordner geholt und in der Akte abgelegt.
        </span>
      )}
      {!registerMoeglich && registerFehltHinweis && !ausRegister && (
        <span className={s.hinweis}>{registerFehltHinweis}</span>
      )}
    </div>
  );
}
